/**
 * Authenticated attachment <img> with a graceful failure state.
 *
 * Why this exists: the /api/attachments/:id/download endpoint requires a
 * JWT, but <img> tags can't send an Authorization header — the token must
 * ride in the URL (?token=...). Several photo grids hardcoded the bare
 * path and every thumbnail 401'd into the browser's broken-image icon.
 * Centralizing the URL construction here makes that mistake impossible,
 * and the onError fallback covers the other failure mode (attachment
 * rows whose storage blob is gone) with a labeled placeholder instead of
 * the raw blue "?" icon.
 */
import { useState } from "react";
import { ImageOff } from "lucide-react";
import { attachmentDownloadUrl } from "@/lib/api";
import { cn } from "@/lib/utils";

interface AttachmentImageProps {
    attachmentId: string;
    alt: string;
    /** "thumb" serves the ~400px cached JPEG — use it for grids. */
    variant?: "thumb" | "full";
    className?: string;
    /** Applied to the fallback placeholder when the image fails to load. */
    fallbackClassName?: string;
    loading?: "lazy" | "eager";
    onClick?: (e: React.MouseEvent) => void;
}

export function AttachmentImage({
    attachmentId,
    alt,
    variant = "thumb",
    className,
    fallbackClassName,
    loading,
    onClick,
}: AttachmentImageProps) {
    const [failed, setFailed] = useState(false);

    if (failed) {
        return (
            <div
                data-testid="attachment-image-fallback"
                className={cn(
                    "flex h-full w-full flex-col items-center justify-center gap-1 bg-muted text-muted-foreground",
                    fallbackClassName,
                )}
                title={`${alt} — the photo record exists but its file didn't load (deleted from storage or still uploading from the tech's phone). Retry later or check the job's attachments.`}
                onClick={onClick}
            >
                <ImageOff className="h-5 w-5" aria-hidden="true" />
                <span className="px-1 text-center text-[10px] leading-tight">Photo unavailable — file missing</span>
            </div>
        );
    }

    const src =
        variant === "thumb"
            ? attachmentDownloadUrl(attachmentId, { variant: "thumb" })
            : attachmentDownloadUrl(attachmentId, { disposition: "inline" });

    return (
        <img
            src={src}
            alt={alt}
            loading={loading}
            className={className}
            onClick={onClick}
            onError={() => setFailed(true)}
        />
    );
}
